import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Select, Space, Spin, Switch, Table, Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);
import type { ColumnsType } from 'antd/es/table';
import { DatabaseOutlined, ReloadOutlined, DownloadOutlined, DeleteOutlined, WifiOutlined } from '@ant-design/icons';
import SearchBox from 'components/molecules/SearchBox';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import { adminUrls } from 'config/urls';
import { sendNotification } from 'utils/helperMethods';
import { useQueryClient } from '@tanstack/react-query';
import _ from 'lodash';
import { useFillHeightToFitScreen } from 'hooks/general/useFillHeightToFitScreen';

const REDIS_KEYS_QUERY_KEY = 'admin-redis-keys';
const DEBOUNCE_MS = 500;
const AUTO_REFRESH_INTERVAL_MS = 30000;
const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 50;
const mainSectionId = 'cache-management-page';
export type RedisKeyRow = {
    key: string;
    type: string;
    ttl: number | null;
    expiryStatus: string;
    memoryKB: number;
    createdAt: string;
};

type RedisKeysResponse = {
    totalKeys: number;
    memoryUsedKB: number;
    memoryMaxMB: number;
    page: number;
    limit: number;
    data: RedisKeyRow[];
};

function getTtlBadgeColor(ttl: number | null, expiryStatus: string): string {
    if (expiryStatus === 'expired') return 'red';
    if (ttl === null) return 'default';
    if (ttl >= 300) return 'green';
    return 'orange';
}

function formatTtlDisplay(ttl: number | null, expiryStatus: string): string {
    if (expiryStatus === 'expired') return 'Expired';
    if (ttl === null) return 'No Expiry';
    return `${ttl}s`;
}

function parseRedisKeysResponse(payload: any): RedisKeysResponse {
    if (!payload || !payload?.data) {
        return {
            totalKeys: 0,
            memoryUsedKB: 0,
            memoryMaxMB: 0,
            page: DEFAULT_PAGE,
            limit: DEFAULT_LIMIT,
            data: [],
        };
    }
    return {
        totalKeys: payload.totalKeys ?? 0,
        memoryUsedKB: payload.memoryUsedKB ?? 0,
        memoryMaxMB: payload.memoryMaxMB ?? 0,
        page: payload.page ?? DEFAULT_PAGE,
        limit: payload.limit ?? DEFAULT_LIMIT,
        data: Array.isArray(payload.data) ? payload.data : [],
    };
}

const columns: ColumnsType<RedisKeyRow> = [
    {
        title: 'Key',
        dataIndex: 'key',
        key: 'key',
        ellipsis: true,
        render: (text: string) => <span className="font-mono text-sm">{text}</span>,
    },
    {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        width: 100,
    },
    {
        title: 'TTL',
        key: 'ttl',
        width: 120,
        render: (_: unknown, record: RedisKeyRow) => formatTtlDisplay(record.ttl, record.expiryStatus),
    },
    {
        title: 'Expiry Status',
        dataIndex: 'expiryStatus',
        key: 'expiryStatus',
        width: 110,
        render: (_: unknown, record: RedisKeyRow) => (
            <Tag color={getTtlBadgeColor(record.ttl, record.expiryStatus)}>{record.expiryStatus}</Tag>
        ),
    },
    {
        title: 'Memory (KB)',
        dataIndex: 'memoryKB',
        key: 'memoryKB',
        width: 110,
        align: 'right',
    },
    {
        title: 'Created At',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 140,
        render: (createdAt: string) => {
            if (!createdAt) return '—';
            const d = dayjs(createdAt);
            const relative = d.fromNow();
            const full = d.format('YYYY-MM-DD HH:mm:ss');
            return (
                <Tooltip title={full}>
                    <span>{relative}</span>
                </Tooltip>
            );
        },
    },
];

const CacheManagement = () => {
    const queryClient = useQueryClient();
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState<string>('all');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [page, setPage] = useState(DEFAULT_PAGE);
    const [limit, setLimit] = useState(DEFAULT_LIMIT);
    const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

    const mainSectionHeight = useRef<number | null>(0);

    //this hook will give height to fill the screen height
    mainSectionHeight.current = useFillHeightToFitScreen({
        id: mainSectionId,
        height: mainSectionHeight.current,
        buffer: 45, //for padding at the bottom, change to 16 once testing is done
        triggerDummyRender: true,
    });

    const debouncedSetSearch = useMemo(
        () =>
            _.debounce((val: string) => {
                setDebouncedSearch(val || '');
                setPage(1);
            }, DEBOUNCE_MS),
        [],
    );

    useEffect(() => {
        debouncedSetSearch(search);
        return () => debouncedSetSearch.cancel();
    }, [search, debouncedSetSearch]);

    const { data, loading, error, isError } = useCommonApiHandler({
        url: adminUrls.getRedisKeys,
        method: 'get',
        queryKey: [REDIS_KEYS_QUERY_KEY, debouncedSearch, typeFilter, statusFilter, page, limit],
        paramsVariables: {
            ...(debouncedSearch ? { search: debouncedSearch } : {}),
            ...(typeFilter !== 'all' ? { type: typeFilter } : {}),
            ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
            page,
            limit,
        },
        parserFunction: (payload: any) => parseRedisKeysResponse(payload),
        queryConfig: {},
    });

    useEffect(() => {
        if (error || isError) {
            sendNotification('error', typeof error === 'string' ? error : 'Failed to fetch Redis keys');
        }
    }, [error, isError]);

    const onSearch = useCallback((val: string) => setSearch(val ?? ''), []);

    const handleRefresh = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: [REDIS_KEYS_QUERY_KEY] });
    }, [queryClient]);

    const handleTableChange = useCallback((pageNum: number, pageSize: number) => {
        setPage(pageNum);
        setLimit(pageSize);
        setSelectedRowKeys([]);
    }, []);

    const { mutate: flushMutate, loading: flushLoading } = useCommonMutationHandler({
        url: adminUrls.flushRedisKeys,
        method: 'post',
        onSuccess: () => {
            sendNotification('success', 'Keys flushed successfully');
            setSelectedRowKeys([]);
            queryClient.invalidateQueries({ queryKey: [REDIS_KEYS_QUERY_KEY] });
        },
    });

    const handleFlushSelected = useCallback(() => {
        if (selectedRowKeys.length === 0) return;
        flushMutate({ keys: selectedRowKeys }, { onError: () => sendNotification('error', 'Flush failed') });
    }, [selectedRowKeys, flushMutate]);

    const handleFlushAll = useCallback(() => {
        const rows = data?.data ?? [];
        const keysToFlush = rows.map((r: RedisKeyRow) => r.key);
        if (keysToFlush.length === 0) {
            sendNotification('info', 'No keys to flush');
            return;
        }
        flushMutate(
            { keys: keysToFlush },
            {
                onError: () => sendNotification('error', 'Flush failed'),
            },
        );
    }, [data?.data, flushMutate]);

    const totalKeys = data?.totalKeys ?? 0;
    const memoryUsedKB = data?.memoryUsedKB ?? 0;
    const memoryMaxMB = data?.memoryMaxMB ?? 0;
    const rows = data?.data ?? [];
    const currentPage = data?.page ?? page;
    const currentLimit = data?.limit ?? limit;

    return (
        <div
            style={{ height: mainSectionHeight.current ? mainSectionHeight.current : 'auto' }}
            id={mainSectionId}
            className="p-6 bg-white min-h-full"
        >
            <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-violetAlt flex items-center justify-center flex-shrink-0">
                        <DatabaseOutlined className="text-white text-lg" />
                    </div>
                    <div>
                        <h1 className="text-xl font-semibold text-fontColor m-0">Redis Cache Management</h1>
                        <p className="text-sm text-greyFont m-0 mt-0.5">View and manage Redis cache keys</p>
                    </div>
                </div>
                <Space size={[8, 8]} wrap>
                    <Tag color="purple">Admin Access Only</Tag>
                    <Tag>Role: Admin</Tag>
                    <Tag color="blue">Dev</Tag>
                    <Tag color="success" icon={<WifiOutlined />}>
                        Connected
                    </Tag>
                </Space>
            </div>

            <div className="flex flex-wrap gap-4 mb-6">
                <Card className="flex-1 min-w-[140px] !rounded-lg" size="small">
                    <p className="text-xs font-medium text-greyFont uppercase tracking-wide m-0 mb-1">Total Keys</p>
                    <p className="text-2xl font-semibold text-fontColor m-0">{totalKeys}</p>
                </Card>
                <Card className="flex-1 min-w-[140px] !rounded-lg" size="small">
                    <p className="text-xs font-medium text-greyFont uppercase tracking-wide m-0 mb-1">Memory Used</p>
                    <p className="text-2xl font-semibold text-fontColor m-0">
                        {memoryUsedKB >= 1024 ? `${(memoryUsedKB / 1024).toFixed(2)} MB` : `${memoryUsedKB} KB`}
                    </p>
                </Card>
                <Card className="flex-1 min-w-[140px] !rounded-lg" size="small">
                    <p className="text-xs font-medium text-greyFont uppercase tracking-wide m-0 mb-1">Memory Max</p>
                    <p className="text-2xl font-semibold text-fontColor m-0">{memoryMaxMB} MB</p>
                </Card>
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-4">
                <SearchBox
                    search={search}
                    setSearch={setSearch}
                    onSearch={onSearch}
                    placeholder="Search by key name..."
                />
                {/* <Select
                    value={typeFilter}
                    onChange={(v) => {
                        setTypeFilter(v);
                        setPage(1);
                    }}
                    options={[{ value: 'all', label: 'All Types' }]}
                    style={{ width: 120 }}
                />
                <Select
                    value={statusFilter}
                    onChange={(v) => {
                        setStatusFilter(v);
                        setPage(1);
                    }}
                    options={[{ value: 'all', label: 'All Status' }]}
                    style={{ width: 120 }}
                /> */}
                {/* <Space className="ml-2">
                    <span className="text-sm text-[#374151]">Auto-refresh</span>
                    <Switch checked={autoRefresh} onChange={setAutoRefresh} />
                </Space> */}
                <Space className="ml-auto">
                    {selectedRowKeys.length > 0 && (
                        <Button
                            type="primary"
                            danger
                            icon={<DeleteOutlined />}
                            loading={flushLoading}
                            onClick={handleFlushSelected}
                        >
                            Flush selected ({selectedRowKeys.length})
                        </Button>
                    )}
                    <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading}>
                        Refresh
                    </Button>
                    <Button
                        type="primary"
                        danger
                        icon={<DeleteOutlined />}
                        loading={flushLoading}
                        disabled={loading || totalKeys === 0}
                        onClick={handleFlushAll}
                    >
                        Flush All
                    </Button>
                </Space>
            </div>

            <Spin spinning={loading}>
                <Table<RedisKeyRow>
                    rowKey="key"
                    rowSelection={{
                        selectedRowKeys,
                        onChange: (keys) => setSelectedRowKeys(keys as string[]),
                    }}
                    columns={columns}
                    dataSource={rows}
                    pagination={{
                        current: currentPage,
                        pageSize: currentLimit,
                        total: totalKeys,
                        showSizeChanger: true,
                        pageSizeOptions: ['50', '20', '10'],
                        showTotal: (total) => `Total ${total} keys`,
                        onChange: handleTableChange,
                    }}
                    size="small"
                    scroll={{
                        x: 800,
                        y: isNaN(Number(mainSectionHeight.current)) ? 500 : Number(mainSectionHeight.current) - 370,
                    }}
                />
            </Spin>
        </div>
    );
};

export default CacheManagement;
